import {
  FIELD_TYPES,
  formatNumber,
  numberStats,
  SYSTEM_KEYS,
  type CollectionData,
  type FieldDef,
  type RecordData,
  type ViewDef,
} from '@tomo/core';
import { Button, Checkbox, ContextMenu, cx, IconButton, Menu, Tooltip, useConfirm, type MenuItem } from '@tomo/ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AppWindow, ArrowDown, ArrowUp, Copy, EyeOff, Pencil, Plus, Settings2, Trash2, X } from 'lucide-react';
import { memo, useCallback, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useProject, useReadOnly } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import { FieldCell } from '../fields/FieldCell.tsx';
import { AddFieldPopover, FieldSettingsDialog } from '../fields/FieldSettings.tsx';
import { fieldWidth, itemNoun, TITLE_WIDTH, visibleFields } from './view-utils.ts';

const ROW_H = 38;
const SELECT_W = 36;
const ADD_W = 44;

export interface CollectionViewProps {
  collection: CollectionData;
  view: ViewDef;
  records: RecordData[];
  onOpen: (recordId: string) => void;
  onCreate: (values?: Record<string, unknown>) => string;
  empty: ReactNode;
}

export function TableView({ collection, view, records, onOpen, onCreate, empty }: CollectionViewProps) {
  const project = useProject();
  const confirm = useConfirm();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [settingsField, setSettingsField] = useState<string | null>(null);
  const [liveWidths, setLiveWidths] = useState<Record<string, number>>({});
  const fields = visibleFields(collection, view);
  // Viewers get a plain, read-only grid: no selection, header menus, resizing or new rows.
  const readOnly = useReadOnly();
  const selectW = readOnly ? 0 : SELECT_W;
  const addW = readOnly ? 0 : ADD_W;

  const widthOf = useCallback((f: FieldDef) => liveWidths[f.id] ?? fieldWidth(view, f), [liveWidths, view]);
  const titleWidth = liveWidths[SYSTEM_KEYS.title] ?? view.fieldWidths[SYSTEM_KEYS.title] ?? TITLE_WIDTH;
  const totalWidth = selectW + titleWidth + fields.reduce((s, f) => s + widthOf(f), 0) + addW;

  const virtualizer = useVirtualizer({
    count: records.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });

  const sortOf = (fieldId: string) => view.sort.find((s) => s.fieldId === fieldId);
  const setSort = (fieldId: string, dir: 'asc' | 'desc') => project.updateView(collection.id, view.id, { sort: [{ fieldId, dir }, ...view.sort.filter((s) => s.fieldId !== fieldId)] });

  const startResize = (key: string, startWidth: number) => (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    let w = startWidth;
    const move = (ev: PointerEvent) => {
      w = Math.max(80, Math.min(640, startWidth + ev.clientX - x0));
      setLiveWidths((m) => ({ ...m, [key]: w }));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      project.updateView(collection.id, view.id, { fieldWidths: { ...view.fieldWidths, [key]: Math.round(w) } });
      setLiveWidths((m) => {
        const { [key]: _drop, ...rest } = m;
        return rest;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const toggle = useCallback((id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }), []);

  const newRow = () => {
    const id = onCreate();
    setEditingTitle(id);
    requestAnimationFrame(() => {
      const idx = project.listRecords(collection.id).findIndex((r) => r.id === id);
      if (idx >= 0) virtualizer.scrollToIndex(records.length, { align: 'end' });
    });
  };

  const selectedIds = [...selected].filter((id) => records.some((r) => r.id === id));
  const allSelected = records.length > 0 && selectedIds.length === records.length;

  return (
    <div className="table-wrap">
      <div
        className="table-scroll"
        ref={scrollRef}
        // The frozen title column only gets a divider once other columns slide under it.
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollLeft > 0) el.dataset.scrolledX = '';
          else delete el.dataset.scrolledX;
        }}
      >
        <div className="table" style={{ width: totalWidth, minWidth: '100%', ['--select-w' as string]: `${selectW}px` }} role="grid" aria-rowcount={records.length}>
          <div className="table__head" role="row">
            {!readOnly && (
              <div className="th th--select" style={{ width: SELECT_W }}>
                <Checkbox size="sm" checked={allSelected} indeterminate={!allSelected && selectedIds.length > 0} onCheckedChange={(c) => setSelected(c ? new Set(records.map((r) => r.id)) : new Set())} aria-label="Select all" />
              </div>
            )}
            <div className="th th--title" style={{ width: titleWidth }}>
              {readOnly ? (
                <HeaderLabel name={collection.titleLabel} dir={sortOf(SYSTEM_KEYS.title)?.dir} />
              ) : (
                <>
                  <Menu
                    trigger={
                      <button type="button" className="th__button">
                        <span className="g-truncate">{collection.titleLabel}</span>
                        <SortMark dir={sortOf(SYSTEM_KEYS.title)?.dir} />
                      </button>
                    }
                    items={[
                      { label: 'Sort A → Z', icon: <ArrowUp />, onSelect: () => setSort(SYSTEM_KEYS.title, 'asc') },
                      { label: 'Sort Z → A', icon: <ArrowDown />, onSelect: () => setSort(SYSTEM_KEYS.title, 'desc') },
                    ]}
                  />
                  <span className="th__resize" onPointerDown={startResize(SYSTEM_KEYS.title, titleWidth)} />
                </>
              )}
            </div>
            {fields.map((f) => (
              <div key={f.id} className="th" style={{ width: widthOf(f) }} data-numeric={FIELD_TYPES[f.type].numeric || undefined}>
                {readOnly ? (
                  <HeaderLabel name={f.name} dir={sortOf(f.id)?.dir} title={f.description} />
                ) : (
                  <>
                    <Menu
                      trigger={
                        <button type="button" className="th__button" title={f.description || undefined}>
                          <span className="g-truncate">{f.name}</span>
                          <SortMark dir={sortOf(f.id)?.dir} />
                        </button>
                      }
                      items={[
                        { label: 'Edit field…', icon: <Settings2 />, onSelect: () => setSettingsField(f.id) },
                        { type: 'separator' },
                        { label: 'Sort ascending', icon: <ArrowUp />, onSelect: () => setSort(f.id, 'asc') },
                        { label: 'Sort descending', icon: <ArrowDown />, onSelect: () => setSort(f.id, 'desc') },
                        { label: 'Hide in this view', icon: <EyeOff />, onSelect: () => project.updateView(collection.id, view.id, { hiddenFields: [...view.hiddenFields, f.id] }) },
                        ...(f.type !== 'file'
                          ? ([
                              { type: 'separator' },
                              {
                                label: 'Delete field',
                                icon: <Trash2 />,
                                danger: true,
                                onSelect: async () => {
                                  if (await confirm({ title: `Delete “${f.name}”?`, description: 'Its values are removed from every record.', confirmLabel: 'Delete field', danger: true })) {
                                    project.removeField(collection.id, f.id);
                                  }
                                },
                              },
                            ] as MenuItem[])
                          : []),
                      ]}
                    />
                    <span className="th__resize" onPointerDown={startResize(f.id, widthOf(f))} />
                  </>
                )}
              </div>
            ))}
            {!readOnly && (
              <div className="th th--add" style={{ width: ADD_W }}>
                <AddFieldPopover
                  collection={collection}
                  onCreated={(id, needsSetup) => needsSetup && setSettingsField(id)}
                  trigger={
                    <IconButton label="Add field" size="sm">
                      <Plus />
                    </IconButton>
                  }
                />
              </div>
            )}
          </div>

          {records.length === 0 ? (
            <div className="table__empty" style={{ width: 'min(100%, 100vw - 300px)' }}>
              {empty}
            </div>
          ) : (
            <div className="table__body" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((vr) => {
                const r = records[vr.index]!;
                return (
                  <Row
                    key={r.id}
                    record={r}
                    collection={collection}
                    fields={fields}
                    widths={fields.map(widthOf)}
                    titleWidth={titleWidth}
                    top={vr.start}
                    // Parity comes from the row's index, not the DOM: virtualized rows mount out of order.
                    alt={vr.index % 2 === 1}
                    selected={selected.has(r.id)}
                    onToggle={toggle}
                    onOpen={onOpen}
                    editingTitle={editingTitle === r.id}
                    onEditTitle={setEditingTitle}
                    readOnly={readOnly}
                  />
                );
              })}
            </div>
          )}

          <div className="table__foot">
            <div className="tf tf--new" style={{ width: selectW + titleWidth }}>
              {!readOnly && (
                <button type="button" className="table__new" onClick={newRow}>
                  <Plus size={15} /> New {itemNoun(collection)}
                </button>
              )}
            </div>
            {fields.map((f) => (
              <div key={f.id} className="tf" style={{ width: widthOf(f) }}>
                <Aggregate field={f} records={records} collection={collection} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulkbar" role="toolbar" aria-label="Selection actions">
          <span className="bulkbar__count">{selectedIds.length} selected</span>
          <Button
            size="sm"
            variant="ghost"
            icon={<Copy />}
            onClick={() => {
              project.transact(() => selectedIds.forEach((id) => project.duplicateRecord(id)));
              setSelected(new Set());
            }}
          >
            Duplicate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 />}
            onClick={() => {
              project.trashRecord(...selectedIds);
              setSelected(new Set());
            }}
          >
            Move to trash
          </Button>
          <IconButton label="Clear selection" size="sm" onClick={() => setSelected(new Set())}>
            <X />
          </IconButton>
        </div>
      )}

      <FieldSettingsDialog collection={collection} fieldId={settingsField} onClose={() => setSettingsField(null)} />
    </div>
  );
}

/** Column header without the menu (read-only tables). */
function HeaderLabel({ name, dir, title }: { name: string; dir?: 'asc' | 'desc'; title?: string }) {
  return (
    <span className="th__button" data-static="" title={title || undefined}>
      <span className="g-truncate">{name}</span>
      <SortMark dir={dir} />
    </span>
  );
}

function SortMark({ dir }: { dir?: 'asc' | 'desc' }) {
  if (!dir) return null;
  return <span className="th__sort">{dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>;
}

interface RowProps {
  record: RecordData;
  collection: CollectionData;
  fields: FieldDef[];
  widths: number[];
  titleWidth: number;
  top: number;
  alt: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  editingTitle: boolean;
  onEditTitle: (id: string | null) => void;
  readOnly: boolean;
}

const Row = memo(function Row({ record: r, collection, fields, widths, titleWidth, top, alt, selected, onToggle, onOpen, editingTitle, onEditTitle, readOnly }: RowProps) {
  const project = useProject();
  const items: MenuItem[] = [{ label: 'Open', icon: <AppWindow />, onSelect: () => onOpen(r.id) }];
  if (!readOnly) {
    items.push(
      { label: 'Rename', icon: <Pencil />, onSelect: () => onEditTitle(r.id) },
      { label: 'Duplicate', icon: <Copy />, onSelect: () => project.duplicateRecord(r.id) },
      { type: 'separator' },
      { label: 'Move to trash', icon: <Trash2 />, danger: true, onSelect: () => project.trashRecord(r.id) },
    );
  }
  return (
    <ContextMenu items={items}>
      <div className={cx('tr', selected && 'tr--selected')} data-alt={alt || undefined} role="row" style={{ transform: `translateY(${top}px)` }}>
        {!readOnly && (
          <div className="td td--select" style={{ width: SELECT_W }}>
            <Checkbox size="sm" checked={selected} onCheckedChange={() => onToggle(r.id)} aria-label={`Select ${r.title || 'record'}`} />
          </div>
        )}
        <div className="td td--title" style={{ width: titleWidth }}>
          {editingTitle ? (
            <input
              className="td__title-input"
              autoFocus
              defaultValue={r.title}
              placeholder={`${collection.titleLabel}…`}
              onBlur={(e) => {
                const v = e.currentTarget.value.trim();
                if (v !== r.title) project.updateRecord(r.id, { title: v });
                onEditTitle(null);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== 'Escape') return;
                if (e.key === 'Escape') e.currentTarget.value = r.title;
                // Hand the keyboard back to this cell's title, never another row's.
                const cell = e.currentTarget.closest('.td--title');
                e.currentTarget.blur();
                requestAnimationFrame(() => (cell?.querySelector('.td__title') as HTMLElement | null)?.focus());
              }}
            />
          ) : (
            /* The title is the record's name: clicking it renames in place, the window icon opens it. */
            <button
              type="button"
              className="td__title"
              data-editable={!readOnly || undefined}
              // Taking focus here would flash the cell's focus ring for the instant before the
              // input mounts; the input focuses itself.
              onMouseDown={(e) => !readOnly && e.button === 0 && e.preventDefault()}
              onClick={() => (readOnly ? onOpen(r.id) : onEditTitle(r.id))}
            >
              {r.icon && <Icon token={r.icon} size={15} className="td__icon" />}
              <span className={cx('g-truncate', !r.title && 'value-empty')}>{r.title || 'Untitled'}</span>
            </button>
          )}
          {/* Stays put while renaming, so opening the record is always one click away. */}
          <Tooltip content="Open">
            <button type="button" className="td__peek" aria-label="Open" onClick={() => onOpen(r.id)}>
              <AppWindow size={14} />
            </button>
          </Tooltip>
        </div>
        {fields.map((f, i) => (
          <div key={f.id} className="td" style={{ width: widths[i] }} data-type={f.type}>
            <FieldCell record={r} field={f} collection={collection} readOnly={readOnly} />
          </div>
        ))}
      </div>
    </ContextMenu>
  );
});

function Aggregate({ field, records, collection }: { field: FieldDef; records: RecordData[]; collection: CollectionData }) {
  const project = useProject();
  const stats = useMemo(() => {
    if (!FIELD_TYPES[field.type].numeric || field.type === 'checkbox') return null;
    return numberStats(records, field, { ...project.valueContext(), fields: collection.fields });
  }, [field, records, project, collection.fields]);
  if (field.type === 'checkbox') {
    const n = records.filter((r) => r.values[field.id] === true).length;
    return records.length ? <span className="agg">{n}/{records.length}</span> : null;
  }
  if (!stats) return null;
  const f = (n: number) => formatNumber(Math.round(n * 100) / 100, { ...field.config, decimals: field.config.decimals ?? (Number.isInteger(n) ? 0 : 1) });
  return (
    <Tooltip
      content={
        <span className="tabular">
          sum {f(stats.sum)} · min {f(stats.min)} · max {f(stats.max)}
        </span>
      }
    >
      <span className="agg">
        <span className="agg__label">avg</span> {f(stats.avg)}
      </span>
    </Tooltip>
  );
}
